import { useEffect, useMemo, useState } from "react";
import { History, ShieldCheck, ShieldAlert, Filter, Search, RefreshCcw } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";

const STATUS_FILTERS = [
  { id: "all", label: "All events" },
  { id: "success", label: "Successful" },
  { id: "failed", label: "Failed" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function parseDevice(ua) {
  if (!ua) return "Unknown device";
  if (/iPhone|iPad/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh|Mac OS X/.test(ua)) return "macOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return "Web";
}

function parseBrowser(ua) {
  if (!ua) return "—";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return "Safari";
  return "Other";
}

export default function AuditLogs() {
  useSeo({ title: "Audit Logs", description: "Login activity, IPs and device history for the assessment portal.", path: "/admin/audit-logs" });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs?limit=500");
      setLogs(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return logs.filter((l) => {
      if (filter === "success" && !l.success) return false;
      if (filter === "failed" && l.success) return false;
      if (!needle) return true;
      return (l.identifier || "").toLowerCase().includes(needle)
        || (l.ip || "").toLowerCase().includes(needle)
        || (l.user_agent || "").toLowerCase().includes(needle)
        || (l.reason || "").toLowerCase().includes(needle);
    });
  }, [logs, filter, q]);

  const stats = useMemo(() => {
    const success = logs.filter((l) => l.success).length;
    return { total: logs.length, success, failed: logs.length - success };
  }, [logs]);

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <History className="w-6 h-6 text-blue-600" /> Login audit log
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Tracks every login attempt with date, time, IP address and device for security review.
          </p>
        </div>
        <button
          data-testid="audit-refresh-btn"
          onClick={load}
          className="inline-flex items-center gap-2 text-sm border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-lg px-3 py-2 text-slate-700 transition"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard tone="slate" label="Total events" value={stats.total} icon={History} />
        <StatCard tone="emerald" label="Successful sign-ins" value={stats.success} icon={ShieldCheck} />
        <StatCard tone="red" label="Failed attempts" value={stats.failed} icon={ShieldAlert} />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="audit-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, IP or device…"
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1">
            <Filter className="w-4 h-4 text-slate-500 ml-1.5" />
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`audit-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`text-xs px-2.5 py-1 rounded-md transition ${
                  filter === f.id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >{f.label}</button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-logs-table">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">When</th>
                <th className="text-left px-4 py-2.5 font-medium">User</th>
                <th className="text-left px-4 py-2.5 font-medium">IP</th>
                <th className="text-left px-4 py-2.5 font-medium">Device</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan="5" className="text-center py-10 text-slate-400">Loading audit log…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="5" className="text-center py-10 text-slate-400">No events match your filters.</td></tr>
              )}
              {!loading && filtered.map((l) => (
                <tr key={l.id || `${l.identifier}-${l.at}`} className="hover:bg-slate-50" data-testid="audit-log-row">
                  <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{fmtTime(l.at)}</td>
                  <td className="px-4 py-2.5 text-slate-900">{l.identifier || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-700 font-mono-acc">{l.ip || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-700">
                    <div className="font-medium">{parseDevice(l.user_agent)}</div>
                    <div className="text-xs text-slate-500">{parseBrowser(l.user_agent)}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    {l.success ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                        <ShieldCheck className="w-3 h-3" /> Success
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5" title={l.reason || ""}>
                        <ShieldAlert className="w-3 h-3" /> Failed
                      </span>
                    )}
                    {!l.success && l.reason && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{l.reason}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const toneMap = {
  slate:   { ring: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700" },
  emerald: { ring: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700" },
  red:     { ring: "border-red-200",     bg: "bg-red-50",     text: "text-red-700" },
};

function StatCard({ tone, label, value, icon: Icon }) {
  const t = toneMap[tone] || toneMap.slate;
  return (
    <div className={`bg-white border ${t.ring} rounded-xl p-4 flex items-center gap-3`}>
      <div className={`w-10 h-10 rounded-lg ${t.bg} grid place-items-center ${t.text}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-semibold text-slate-900" data-testid={`stat-${tone}`}>{value}</div>
      </div>
    </div>
  );
}
